// React
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import { Badge, Card, Fade, Icon, Modal, Tooltip } from "@mui/material";

import { gql, useLazyQuery } from "@apollo/client";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import CustomPagination from "components/Pagination";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import DataTable from "components/Tables/DataTable";
import expedientesData from "../../data/expedientesData";
import { UserContext } from "context/user";
import { getUproColor } from "utils/colors";
import SoftButton from "components/SoftButton";
import AdministrarPedido from "layouts/compras/components/AdministrarPedido";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "70%" },
  overflow: "auto",
};

export default function ModalExpedientesSucursal({ open, handleClose }) {
  const { user } = useContext(UserContext);
  const [prioritario, setPrioritario] = useState(false);
  const [showAllExpedientes, setShowAllExpedientes] = useState(false);
  const [rows, setRows] = useState([]);
  const { columns, getRows } = expedientesData;
  const [modals, setModals] = useState({
    id: null,
    expediente: false,
  });
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [order, setOrder] = useState({
    campo: "id",
    orden: "DESC",
  });
  const [getExpedientes, { loading, data: pedidos, refetch }] = useLazyQuery(
    gql`
      query GET_EXPEDIENTES(
        $filter: JSON
        $pagination: PaginationInput
        $order: [Order]
        $search: String
        $prioritario: AreaExpedienteEnum
      ) {
        paginationInfo {
          pages
          total
        }
        pedidosCompra(filter: $filter, pagination: $pagination, order: $order, search: $search, prioritario: $prioritario) {
          id
          nombre
          tipoCompra
          numeroExpediente
          estado
          licitacionEstado
          createdAt
          motivoAnulacion
          fechaAnulacion
          stockEstado
          estadoBase {
            nombre
            area
          }
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
    }
  );

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  useEffect(() => {
    if (open) {
      getExpedientes({
        variables: {
          filter: showAllExpedientes ? {} : {
            createdUser: user?.id,
          },
          pagination: {
            page: page,
            limit: 10,
          },
          order: [{ field: order.campo, order: order.orden }],
          search: search || undefined,
          prioritario: prioritario ? "Solicitante" : undefined,
        },
      });
    }
  }, [open, search, page, order, prioritario, showAllExpedientes]);

  useEffect(() => {
    if (!open) {
      setRows([]);
      setPagesInfo({
        pages: 0,
        total: 0,
      });
      setSearch("");
      setPage(1);
      setShowAllExpedientes(false); // Reset to "my expedientes" by default
    }
  }, [open]);

  useEffect(() => {
    if (pedidos?.pedidosCompra) {
      setRows(getRows(pedidos?.pedidosCompra, handleClick));
      setPagesInfo(pedidos?.paginationInfo);
    }
  }, [pedidos]);

  const handleClick = (e, item) => {
    e.stopPropagation();
    setModals({
      ...modals,
      id: item.id,
      expediente: true,
    });
  };

  const handleOrderBy = (campo) => {
    if (order.campo === campo) {
      if (order.orden === "ASC") {
        setOrder({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrder({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrder({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" p={2} pb={0}>
              <SoftBox>
                <SoftTypography variant="h6">
                  {showAllExpedientes ? "Todos los procesos de compra" : "Mis procesos de compra"}
                </SoftTypography>
                <SoftBox display="flex" alignItems="center" gap={0.5}>
                  <Icon sx={{ color: getUproColor("uproYellow") }}>
                    {showAllExpedientes ? "business" : "person"}
                  </Icon>
                  <SoftTypography variant="caption" fontWeight="medium" color="uproYellow">
                    {showAllExpedientes ? "Todos los usuarios" : `${user?.nombre} ${user?.apellido}`}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>

              <SoftBox display="flex" gap={1} alignItems="center">
                <Tooltip
                  title={showAllExpedientes ? "Ver solo mis expedientes" : "Ver todos los expedientes"}
                  placement="top"
                >
                  <SoftButton
                    color={showAllExpedientes ? "primary" : "secondary"}
                    circular
                    onClick={() => {
                      setShowAllExpedientes(!showAllExpedientes);
                      setPage(1); // Reset page when changing filter
                    }}
                    variant={showAllExpedientes ? "contained" : "outlined"}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>
                      {showAllExpedientes ? "groups" : "person"}
                    </Icon>
                    {showAllExpedientes ? "todos" : "mis exp."}
                  </SoftButton>
                </Tooltip>
                <Tooltip
                  title={prioritario ? "Ver todos" : "Ver expedientes que requieren atención"}
                  placement="top"
                >
                  <Badge badgeContent={0} color="error">
                    <SoftButton
                      color={prioritario ? "secondary" : "uproYellow"}
                      circular
                      onClick={() => {
                        setPrioritario(!prioritario);
                      }}
                      variant={prioritario ? "outlined" : "contained"}
                    >
                      <Icon sx={{ fontWeight: "bold" }}>
                        {prioritario ? "close" : "priority_high"}
                      </Icon>
                      {prioritario ? "ver todos" : "ver importantes"}
                    </SoftButton>
                  </Badge>
                </Tooltip>
                <Tooltip title="Agregar una nueva solicitud" placement="top">
                  <SoftButton
                    circular
                    color="primary"
                    onClick={() => setModals({ ...modals, expediente: true })}
                  >
                    <Icon>add</Icon>
                    nueva solicitud
                  </SoftButton>
                </Tooltip>
                <Tooltip title="Cerrar" placement="top">
                  <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                    close
                  </Icon>
                </Tooltip>
              </SoftBox>
            </SoftBox>
            
            {/* Search Input */}
            <SoftBox px={2} py={2}>
              <SoftInput
                placeholder="Buscar expedientes..."
                onChange={handleSearch}
                icon={{ component: "search", direction: "left" }}
              />
            </SoftBox>

            <SoftBox>
              {loading ? (
                <Loading />
              ) : pedidos?.pedidosCompra?.length > 0 ? (
                <DataTable columns={columns} rows={rows} order={order} setOrder={handleOrderBy} />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={pedidos?.pedidosCompra?.length}
              total={pagesInfo?.total}
              pages={pagesInfo?.pages}
              page={page}
              setPage={setPage}
              noCard
            />
            <AdministrarPedido
              open={modals?.expediente}
              handleClose={(prev) => setModals({ ...prev, expediente: false })}
              refetchParent={refetch}
              id={modals?.id}
              mode="Solicitante"
            />
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalExpedientesSucursal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idSucursal: PropTypes.string || PropTypes.number,
};
